/**
 * Centralized source-of-truth for all academic and industry research metadata.
 * 
 * When a paper's status changes (e.g., preprint → peer-reviewed),
 * update ONLY this file. All components import from here.
 * 
 * Static files (public/llms.txt, public/methodology/index.html) cannot import
 * from TypeScript. Update those manually and reference this file in a comment.
 */

export type SourceStatus = 'peer-reviewed' | 'preprint';

export interface AcademicSource {
  id: string;
  title: string;
  authors: string;
  year: number;
  venue: string;
  status: SourceStatus;
  institution: string;
  finding: string;
  url: string;
}

export interface IndustrySource {
  id: string;
  title: string;
  description: string;
  url: string;
}

// ─── Academic Sources ────────────────────────────────────────────────

export const academicSources: Record<string, AcademicSource> = {
  'kdd-2024': {
    id: 'kdd-2024',
    title: 'GEO: Generative Engine Optimization',
    authors: 'Aggarwal et al.',
    year: 2024,
    venue: 'KDD 2024',
    status: 'peer-reviewed',
    institution: 'Princeton',
    finding: 'GEO can boost visibility up to 40%',
    url: 'https://arxiv.org/abs/2311.09735',
  },
  'neurips-2025': {
    id: 'neurips-2025',
    title: 'C-SEO Bench: SEO vs GEO Effectiveness',
    authors: 'Puerto et al.',
    year: 2025,
    venue: 'NeurIPS 2025',
    status: 'peer-reviewed',
    institution: 'TU Darmstadt, NAVER AI',
    finding: '+2.77 vs +0.36 citation rank improvement (retail, gpt-4o-mini)',
    url: 'https://arxiv.org/abs/2506.11097',
  },
  'yang-2025': {
    id: 'yang-2025',
    title: 'News Source Citing Patterns in AI Search',
    authors: 'Yang',
    year: 2025,
    venue: 'arXiv 2025',
    status: 'preprint',
    institution: 'Northeastern University',
    finding: 'Top 20 news outlets = 67.3% of OpenAI-model news citations',
    url: 'https://arxiv.org/abs/2507.05301',
  },
  'uoft-2025': {
    id: 'uoft-2025',
    title: 'How to Dominate AI Search',
    authors: 'Chen et al.',
    year: 2025,
    venue: 'arXiv 2025',
    status: 'preprint',
    institution: 'University of Toronto',
    finding: '63 to 93 percent',
    url: 'https://arxiv.org/abs/2509.08919',
  },
  'kirsten-2025': {
    id: 'kirsten-2025',
    title: 'Characterizing Web Search in GenAI Age',
    authors: 'Kirsten et al.',
    year: 2025,
    venue: 'arXiv 2025',
    status: 'preprint',
    institution: 'Ruhr University + MPI',
    finding: '~40% unique URLs in AI vs Google',
    url: 'https://arxiv.org/abs/2510.11560',
  },
  'zhang-2025': {
    id: 'zhang-2025',
    title: 'Source Coverage and Citation Bias',
    authors: 'Zhang et al.',
    year: 2025,
    venue: 'arXiv 2025',
    status: 'preprint',
    institution: 'Multi-institutional',
    finding: '37% of AI domains unique from Google',
    url: 'https://arxiv.org/abs/2512.09483',
  },
};

// ─── Industry Sources ────────────────────────────────────────────────

export const industrySources: IndustrySource[] = [
  {
    id: 'semrush',
    title: 'Semrush AI Overviews Study (2025)',
    description: '10M+ keywords analyzed, AI Overview impact data',
    url: 'https://www.semrush.com/blog/semrush-ai-overviews-study/',
  },
  {
    id: 'seranking',
    title: 'SE Ranking AI Statistics (2025)',
    description: 'CTR impacts, zero-click rates, traffic trends',
    url: 'https://seranking.com/blog/ai-statistics/',
  },
  {
    id: 'ahrefs',
    title: 'Ahrefs AI Overviews Impact (2025)',
    description: 'Impact of AI Overviews on organic click-through rates',
    url: 'https://ahrefs.com/blog/ai-overviews-reduce-clicks/',
  },
];

// ─── Derived Lists ───────────────────────────────────────────────────

const allSources = Object.values(academicSources);

export const peerReviewedSources = allSources.filter(s => s.status === 'peer-reviewed');
export const preprintSources = allSources.filter(s => s.status === 'preprint');

/** Unique institutions across all academic sources */
export const allInstitutions: string[] = [
  'Princeton', 'TU Darmstadt', 'NAVER AI', 'Northeastern University', 'U of Toronto',
  'Ruhr University', 'Max Planck Institute',
];

/** Unique venues for badge display (peer-reviewed first, then preprints) */
export const allVenues: string[] = [
  ...new Set([
    ...peerReviewedSources.map(s => s.venue),
    'arXiv 2025', // Collapsed: multiple arXiv preprints
  ]),
];

// ─── Citation Helpers ────────────────────────────────────────────────

/** Returns inline citation string, e.g. "Yang, arXiv 2025" */
export function cite(id: string): string {
  const s = academicSources[id];
  if (!s) return id;
  return `${s.authors}, ${s.venue}`;
}

/** Returns citation with URL for link rendering */
export function citeWithUrl(id: string): { text: string; url: string } {
  const s = academicSources[id];
  if (!s) return { text: id, url: '' };
  return { text: `${s.authors}, ${s.venue}`, url: s.url };
}

/** Returns badge label for a source: "Peer-reviewed ✓" or "Preprint" */
export function badgeLabel(id: string): string {
  const s = academicSources[id];
  if (!s) return '';
  return s.status === 'peer-reviewed' ? 'Peer-reviewed ✓' : 'Preprint';
}

/** Generates JSON-LD citation array for structured data */
export function jsonLdCitations(): object[] {
  return allSources.map(s => ({
    '@type': 'ScholarlyArticle',
    'name': s.title,
    'author': { '@type': 'Person', 'name': s.authors },
    'datePublished': String(s.year),
    'publisher': { '@type': 'Organization', 'name': s.venue },
    'url': s.url,
  }));
}
